import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {IHolidayDetailSolution} from '../../holiday-detail-page/interfaces/iholiday-detail-solution';
import {HolidayBookingService} from '../../holiday-booking-page/services/holiday-booking.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {NgbAccordion} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'holiday-detail-generic-page-solutions',
  templateUrl: './holiday-detail-generic-page-solutions.component.html',
  styleUrls: ['./holiday-detail-generic-page-solutions.component.scss']
})
export class HolidayDetailGenericPageSolutionsComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('solutionsAccordion') solutionsAccordion: NgbAccordion;

  @Input() detailSolutions: IHolidayDetailSolution[];
  @Input() formattedGuestsString: string;
  @Input() allAvailableDates: Date[];

  @Output() livePricesLoadingComplete: EventEmitter<boolean> = new EventEmitter<boolean>();

  private unsubscribe$ = new Subject();
  public selectedSolution: IHolidayDetailSolution;
  public selectedDepartureDate: Date;
  public isLoadingDetail: boolean;
  public accomodationId;
  public accomodationParentId;

  constructor(private _holidayBookingService: HolidayBookingService) { }

  ngOnInit(): void {
    this._holidayBookingService.selectedSolution$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((selectedSolution: IHolidayDetailSolution) => {
        this.selectedSolution = selectedSolution;
        if (this.selectedSolution) {
          this.solutionsAccordion?.expand(`solution-${this.selectedSolution.nights}-${this.selectedSolution.airport || 'solo-soggiorno'}`);
        }

      });


    this._holidayBookingService.selectedDepartureDate$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((departureDate: Date) => {
        this.selectedDepartureDate = departureDate;
      });

    this._holidayBookingService.holidayDetailLoadingStatus$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((loadingStatus) => {
        this.isLoadingDetail = loadingStatus.isLoadingDetail || loadingStatus.isDebounceing;
      });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.detailSolutions && this.detailSolutions?.length) {

      //  Esistono soluzioni apparentemente duplicate che fanno inutilmente lievitare il numero di liveAlpi.
      //  Si cerca di rimuoverle definendo come soluzioni "duplicate" quelle che hanno lo stesso punto di partenza e la stessa durata

      const duplicatesRemoved = [] as IHolidayDetailSolution[];

      //  Ciclo tutte le soluzioni
      this.detailSolutions.forEach((solutionUnchecked: IHolidayDetailSolution) => {
        //  Era già stata filtrata una soluzione con le stesse caratteristiche ?

        const matchingIndex = duplicatesRemoved.findIndex((solutionChecked: IHolidayDetailSolution) => {
          return solutionChecked.nights === solutionUnchecked.nights &&
            ((!solutionChecked.airport && !solutionUnchecked.airport) ||
            (solutionChecked.airport === solutionUnchecked.airport));
        });

        //  No, dunque pusho nell'array quella ciclata
        if (matchingIndex < 0) {
          duplicatesRemoved.push(solutionUnchecked);
          return;
        }
        //  Sì, quindi stabiliamo quale debba rimanere nell'array (la più economica)
        const matching = duplicatesRemoved[matchingIndex];
        let mustReplace: boolean = false;
        //  Confronto i prezzi (che possono essere nel campo price o totalPrice, ma il confronto deve essere fatto sullo stesso campo)
        if (matching.totalPrice && solutionUnchecked.totalPrice) {
          if (matching.totalPrice > solutionUnchecked.totalPrice) {
            mustReplace = true;
          }
        }
        if (matching.price && solutionUnchecked.price) {
          if (matching.price > solutionUnchecked.price) {
            mustReplace = true;
          }
        }
        //  Devo rimpiazzare la solution precedentemente inserita nell'array se quella ciclata risulta più economica
        if (mustReplace) {
          duplicatesRemoved.splice(matchingIndex, 1, solutionUnchecked);
        }
      });

      this.detailSolutions = duplicatesRemoved;

      const allPromises = this.detailSolutions?.map((solution: IHolidayDetailSolution) => {
        return this.findSolutionLivePrice.bind(this, solution);
      });
      this.detailSolutions.forEach((solution: IHolidayDetailSolution) => {
        solution.isLoadingLiveInfo = true;
      });

      this._holidayBookingService.setDetailLoadingStatus(false, true);
      this.getAllSolutionsLivePrices(allPromises);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private findSolutionLivePrice(solution: IHolidayDetailSolution) {
    return this._holidayBookingService.getSolutionAdditionalLiveInfo(solution)
      .catch((err: Error) => {
        solution.liveError = true;
        return null;
      })
      .finally(() => {
        solution.isLoadingLiveInfo = false;
      });
  }

  private getAllSolutionsLivePrices(promises: any[]) {
    if (!promises.length) {
      // tutte le soluzioni sono state ciclate
      this.onLivePricesLoadingComplete();
      return Promise.resolve();
    }
    const currentFn = promises[0];
    return currentFn()
      .then(() => {
        return this.getAllSolutionsLivePrices(promises.slice(1));
      });
  }

  private onLivePricesLoadingComplete() {

    this.livePricesLoadingComplete.emit(true);

    const cheapest = this.detailSolutions
      ?.filter((solution: IHolidayDetailSolution) => !solution.liveError && solution.totalPrice)
      ?.reduce((accumulator, currentSolution) => {
        const currentPrice = currentSolution.totalPrice || Infinity;
        const accumulatorPrice = accumulator.totalPrice || Infinity;
        return currentPrice < accumulatorPrice ? currentSolution : accumulator;
      }, {totalPrice: Infinity} as IHolidayDetailSolution);

    if (cheapest) {
      cheapest.isCheapest = true;
    }

    this._holidayBookingService.setDetailLoadingStatus();
  }

  public onClickOnAlternativeSolution(newSolution) {
    this._holidayBookingService.setData('selectedSolution', newSolution);
  }

  public onClickOnChooseDate(): void {
    this._holidayBookingService.departureDateAccordionTrigger$.next(true);
  }


}
